import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MediaAsset } from '@ghostscreen/contracts';
import { QueryEmptyState } from '../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { api } from '../lib/api';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import { useDeepLinkParam } from '../lib/useDeepLinkParam';
import {
  clampContentTileSize,
  CONTENT_TILE_SIZE_DEFAULT,
  filterContentLibrary,
  groupContentLibraryByUploadDate,
  type ContentLibraryFilter,
} from './content/buildContentLibrary';
import { ContentLibraryLightbox } from './content/ContentLibraryLightbox';
import { ContentLibrarySections } from './content/ContentLibrarySections';
import { ContentLibraryToolbar } from './content/ContentLibraryToolbar';
import { getMediaOperatorStatus } from './content/mediaOperatorStatus';
import { MediaUploader, type MediaUploaderHandle } from './content/MediaUploader';
import { ScreenUploadBanner } from './content/screen-upload/ScreenUploadBanner';
import { useScreenUploadIntent } from './content/screen-upload/useScreenUploadIntent';
import { usePlaylistMediaUsage } from './content/usePlaylistMediaUsage';

const TILE_SIZE_KEY = 'gs-content-tile-size';

function readStoredTileSize(): number {
  try {
    const raw = localStorage.getItem(TILE_SIZE_KEY);
    if (!raw) return CONTENT_TILE_SIZE_DEFAULT;
    return clampContentTileSize(Number(raw));
  } catch {
    return CONTENT_TILE_SIZE_DEFAULT;
  }
}

/**
 * Content = media library. Layout matches Ghost Screen Content.dc.html.
 */
export function ContentPage() {
  const { t, i18n } = useTranslation('content');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const uploaderRef = useRef<MediaUploaderHandle>(null);

  const [filter, setFilter] = useState<ContentLibraryFilter>('all');
  const [tileSize, setTileSize] = useState(readStoredTileSize);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [pageDragging, setPageDragging] = useState(false);

  const mediaQuery = useQuery({
    queryKey: ['media', organisationId],
    queryFn: () => api.listMediaAssets(organisationId),
    refetchInterval: (query) => {
      const rows = query.state.data ?? [];
      return rows.some((asset) => getMediaOperatorStatus(asset).inProgress) ? 4_000 : false;
    },
  });
  const mediaRetry = useQueryRetry(() => mediaQuery.refetch());
  const usage = usePlaylistMediaUsage(organisationId);

  const assets = mediaQuery.data ?? [];
  const screenUpload = useScreenUploadIntent(organisationId, assets);
  const visible = useMemo(() => filterContentLibrary(assets, filter), [assets, filter]);
  const groups = useMemo(
    () => groupContentLibraryByUploadDate(visible),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- group labels are translated inside, so re-run on language change
    [visible, i18n.language],
  );
  const previewAsset = previewId ? (assets.find((asset) => asset.id === previewId) ?? null) : null;

  useDeepLinkParam('assetId', mediaQuery.data, (asset) => setPreviewId(asset.id));

  const invalidateMedia = () =>
    void queryClient.invalidateQueries({ queryKey: ['media', organisationId] });

  useEffect(() => {
    if (location.hash !== '#upload') return;
    uploaderRef.current?.open();
    navigate({ pathname: '/content', search: location.search }, { replace: true });
  }, [location.hash, location.search, navigate]);

  const onTileSizeChange = (size: number) => {
    const next = clampContentTileSize(size);
    setTileSize(next);
    try {
      localStorage.setItem(TILE_SIZE_KEY, String(next));
    } catch {
      /* ignore */
    }
  };

  const openAsset = (asset: MediaAsset) => setPreviewId(asset.id);

  const handleRemoved = () => {
    if (!previewAsset) {
      setPreviewId(null);
      return;
    }
    const remaining = visible.filter((asset) => asset.id !== previewAsset.id);
    const idx = visible.findIndex((asset) => asset.id === previewAsset.id);
    const next = remaining[idx] ?? remaining[idx - 1] ?? null;
    setPreviewId(next?.id ?? null);
  };

  return (
    <div
      className="flex flex-col gap-[22px]"
      style={{ animation: 'gs-in 260ms ease both' }}
      onDragEnter={(event) => {
        event.preventDefault();
        setPageDragging(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setPageDragging(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        if (event.currentTarget === event.target) setPageDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setPageDragging(false);
        if (event.dataTransfer.files?.length) {
          uploaderRef.current?.uploadFiles(event.dataTransfer.files);
        }
      }}
    >
      {pageDragging ? (
        <div
          className="pointer-events-none fixed inset-0 z-40 border-2 border-dashed"
          style={{
            borderColor: 'var(--gs-accent)',
            background: 'color-mix(in srgb, var(--gs-accent) 8%, transparent)',
          }}
          aria-hidden
        />
      ) : null}

      {previewAsset ? (
        <ContentLibraryLightbox
          organisationId={organisationId}
          asset={previewAsset}
          assets={visible}
          membership={usage.membershipFor(previewAsset.id)}
          onClose={() => setPreviewId(null)}
          onSelect={setPreviewId}
          onRemoved={handleRemoved}
        />
      ) : null}

      {screenUpload ? <ScreenUploadBanner intent={screenUpload} /> : null}

      <MediaUploader
        ref={uploaderRef}
        organisationId={organisationId}
        onUploaded={(assetIds) => {
          invalidateMedia();
          screenUpload?.track(assetIds);
        }}
      />

      <ContentLibraryToolbar
        filter={filter}
        tileSize={tileSize}
        onFilterChange={setFilter}
        onTileSizeChange={onTileSizeChange}
      />

      {mediaQuery.isError || mediaRetry.isRetrying ? (
        <QueryErrorState
          message={t('library.loadError')}
          onRetry={mediaRetry.retry}
          isRetrying={mediaRetry.isRetrying}
        />
      ) : mediaQuery.isPending ? (
        <QueryLoadingBlock variant="cards" />
      ) : assets.length === 0 ? (
        <div className="px-5 py-10 text-center text-[14.5px]" style={{ color: 'var(--gs-muted)' }}>
          {t('library.empty')}
        </div>
      ) : visible.length === 0 ? (
        <QueryEmptyState>
          {filter === 'photos'
            ? t('library.emptyPhotos')
            : filter === 'videos'
              ? t('library.emptyVideos')
              : t('library.empty')}
        </QueryEmptyState>
      ) : (
        <ContentLibrarySections
          groups={groups}
          tileSize={tileSize}
          usedMediaIds={usage.usedMediaIds}
          onOpen={openAsset}
        />
      )}

      {assets.length > 0 ? (
        <span
          className="flex items-center gap-2 text-[12.5px]"
          style={{ color: 'var(--gs-muted)' }}
        >
          <span className="h-2 w-2 rounded-full bg-[#E8B23C]" aria-hidden />
          {t('library.unusedLegend')}
        </span>
      ) : null}
    </div>
  );
}
